const cities = [
  { name: "Monroe", zips: "71201, 71202, 71203" },
  { name: "West Monroe", zips: "71291, 71292" },
  { name: "Sterlington", zips: "71280" },
  { name: "Richwood", zips: "71202" },
  { name: "Ruston", zips: "71270, 71272" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your home, clinic, or campus in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function MonroeLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Monroe &amp; Northeast Louisiana
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Monroe anchors Northeast Louisiana's economy as the region's medical, educational, and business hub. The medical corridor along the Ouachita River — centered on St. Francis Medical Center, Ochsner LSU Health Monroe, and a robust network of specialty clinics — employs hundreds of physicians, researchers, and healthcare administrators who attend international medical conferences, engage in global research partnerships, and occasionally take assignments at international teaching hospitals. When a CME conference in Vienna or a global health summit in Geneva is confirmed, passport renewal deadlines do not flex to accommodate standard processing timelines.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              University of Louisiana Monroe (ULM) and Louisiana Delta Community College bring a significant academic population to the region. ULM pharmacy, nursing, and health sciences students participate in international clinical rotations. Business and liberal arts students pursue exchange programs in Europe and Latin America. Graduate students present research at international symposia. For many of these travelers, it is their first international trip — and their first passport application. The combination of first-time applicant complexity and tight academic deadlines creates exactly the kind of urgent situation that Fast Passport Center is built to resolve.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Northeast Louisiana's agricultural economy drives a different travel pattern. Cotton, soybean, and specialty crop producers maintain business relationships with international buyers, seed suppliers, and equipment manufacturers in Brazil, Argentina, and Europe. An agricultural trade mission or commodity buyer meeting can materialize with two weeks notice, and rural Ouachita, Lincoln, and Morehouse Parish families often have fewer passport service options than travelers in major metro areas.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Monroe's position between Shreveport, Dallas, and Jackson also makes it a hub for regional business travel with occasional international connections. Louisiana Pacific, CenturyLink, and other corporations headquartered in or near Monroe generate executive travel to international markets. And Monroe families book Caribbean cruises from New Orleans — just 290 miles south — or fly connections through Dallas-Fort Worth for transatlantic itineraries. Fast Passport Center provides all of these travelers with the same professional, expedited service available to travelers in major U.S. metro areas, without the drive to New Orleans or Houston.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Monroe, LA</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Northeast Louisiana &amp; the Ouachita region</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Remote service — serving Monroe, West Monroe &amp; all of Northeast LA
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Start online, ship overnight from anywhere in Northeast Louisiana</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, <strong className="text-white">ship your documents overnight</strong> from any UPS or FedEx location in Monroe or West Monroe. Most Northeast Louisiana clients complete the entire process without leaving home.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Northeast Louisiana — start online, ship overnight, and we handle the rest. No need to drive to New Orleans or Houston for a regional agency appointment.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Louisiana Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Shreveport", href: "/cities/shreveport" },
                    { name: "Baton Rouge", href: "/cities/baton-rouge" },
                    { name: "New Orleans", href: "/cities/new-orleans" },
                    { name: "Lafayette", href: "/cities/lafayette" },
                    { name: "Lake Charles", href: "/cities/lake-charles" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Monroe Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}